import { SolidHeartIcon, SolidShieldCheckIcon, SolidStarIcon } from '@tempmee/react-icons';
import { getShouldShowBackgroundCheckBadge } from '~/growthbook';
import { BackgroundCheckStatusEnum } from '~/models';
import type { IUser } from '~/models/User';
import { Avatar, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Center } from '../Center';
import { Column } from '../Column';
import { Row } from '../Row';
import { ZStack } from '../ZStack';

interface IProfessionalHeaderProps {
  professional: IUser;
  displayProfession?: string;
}

/**
 * ProfessionalHeader Component
 *
 * Displays professional information including profile image, name, rating, and credentials.
 * Shows favorite status with a heart icon and handles default profile image fallback.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for professional profile display in the ProfessionalProfile module.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/ProfessionalProfile/components/ProfessionalHeader
 *
 * If this is truly only used for professional profile display, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <ProfessionalHeader professional={professionalData} />
 * ```
 */
export const ProfessionalHeader = ({
  professional,
  displayProfession,
}: IProfessionalHeaderProps) => {
  const isFavorite = professional?.isFavorite;

  return (
    <Row
      style={{
        flex: 1,
      }}
    >
      <Center
        data-testid="profile-image-container"
        style={{
          position: 'relative',
          borderRadius: '50%',
          alignItems: 'center',
          justifyContent: 'center',
          border: `3px solid ${isFavorite ? colors.favorites[400] : 'transparent'}`,
          padding: 2,
        }}
      >
        <Avatar size="2xl" src={professional?.profile_image || undefined} alt="profile_image" />
        {isFavorite && (
          <ZStack top="72%" right={-4}>
            <SolidHeartIcon color={colors.favorites[400]} />
          </ZStack>
        )}
      </Center>

      <Center style={{ marginLeft: 20, flex: 1, alignItems: 'flex-start', overflow: 'hidden' }}>
        <Column
          flex={1}
          style={{
            flex: 1,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Row
            style={{
              width: '100%',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Typography as="span" bold size="heading_4" numberOfLines={1}>
              {`${professional?.first_name ?? ''} ${professional?.last_name ?? ''}`.trim()}
            </Typography>

            <Row alignItems="center" style={{ marginLeft: 10 }}>
              <SolidStarIcon size={20} color={colors.success[500]} />
              <Typography as="span" bold style={{ marginRight: 5 }}>
                {professional?.average_rating?.toFixed(1) || '5.0'}
              </Typography>
            </Row>
          </Row>

          <Typography as="span">{displayProfession} Credentials Verified</Typography>
          {professional?.backgroundCheck?.status === BackgroundCheckStatusEnum.Clear &&
            getShouldShowBackgroundCheckBadge() && (
              <Row alignItems="center" gap={4} marginTop={-1}>
                <Typography as="span" style={{ marginTop: 1 }}>Background Checked</Typography>
                <SolidShieldCheckIcon size={24} color={colors.secondary[500]} />
              </Row>
            )}
        </Column>
      </Center>
    </Row>
  );
};
